import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Building2,
  Bell,
  CreditCard,
  Shield,
  Check,
  Zap,
  ExternalLink,
  AlertCircle,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { PlanBadge } from '../../components/ui/Badge'
import { UpgradeModal } from '../../components/shared/UpgradeModal'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'
import { settingsApi, stripeApi } from '../../lib/api'
import { redirectToForgotPassword } from '../../lib/auth'
import { formatDate, formatResetDate, formatNumber } from '../../lib/utils'
import config from '../../config'
import {
  BILLING_PORTAL_CANCELLATION_NOTE,
  formatBillingPlanSummary,
  formatScheduledCancellationMessage,
  getCancelAtPeriodEnd,
  getCurrentPeriodEnd,
  getEffectivePlan,
  getSubscriptionPlan,
  getSubscriptionStatus,
  hasManagedBilling,
  isManualPlanOverride,
} from '../../lib/subscription'
import toast from 'react-hot-toast'

const tabs = [
  { id: 'organisation', label: 'Organisation', icon: Building2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'security', label: 'Security', icon: Shield },
]

const CANCELABLE_STATUSES = ['active', 'trialing', 'past_due']
const PAID_PLANS = ['starter', 'pro', 'business', 'enterprise']

export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || 'organisation'
  )
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab) setActiveTab(tab)
  }, [searchParams])

  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast('Checkout was cancelled. Your current plan has not changed.')
    }
    if (searchParams.get('subscription_cancelled') === 'true') {
      toast.success('Subscription will cancel at the end of the current billing period.')
    }
  }, [searchParams])

  function handleTabChange(tab) {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">
          Manage your organisation, notifications and billing
        </p>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:block">{tab.label}</span>
            </button>
          )
        })}
      </div>

      {activeTab === 'organisation' && <OrganisationTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
      {activeTab === 'billing' && (
        <BillingTab onUpgrade={() => setShowUpgradeModal(true)} />
      )}
      {activeTab === 'security' && <SecurityTab />}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  )
}

function OrganisationTab() {
  const { org, updateOrg, fetchOrg } = useOrgStore()
  const [name, setName] = useState(org?.name || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (org?.name) setName(org.name)
  }, [org])

  async function handleSave(e) {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    const result = await updateOrg({ companyName: name })
    if (result.success) {
      toast.success('Organisation updated')
      fetchOrg()
    } else {
      toast.error(result.error || 'Failed to update')
    }
    setSaving(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5"
    >
      <div className="card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">
          Organisation details
        </h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="form-group">
            <label className="label">Organisation name</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your company name"
            />
          </div>
          <div className="form-group">
            <label className="label">Organisation ID</label>
            <input
              type="text"
              className="input bg-gray-50"
              value={org?.orgId || ''}
              disabled
            />
            <p className="text-xs text-gray-400 mt-1">
              Use this ID when contacting{' '}
              <a
                href="mailto:hello@getworkflowpro.com"
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                hello@getworkflowpro.com
              </a>
              {' '}for support.
            </p>
          </div>
          <div className="form-group">
            <label className="label">Created</label>
            <input
              type="text"
              className="input bg-gray-50"
              value={formatDate(org?.createdAt) || ''}
              disabled
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            loading={saving}
            icon={<Check className="w-4 h-4" />}
          >
            Save changes
          </Button>
        </form>
      </div>
    </motion.div>
  )
}

function NotificationsTab() {
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    settingsApi
      .getNotifications()
      .then((data) => {
        setEmailEnabled(data.emailNotifications ?? true)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  async function handleToggle() {
    const newValue = !emailEnabled
    setSaving(true)
    try {
      await settingsApi.updateNotifications(newValue)
      setEmailEnabled(newValue)
      toast.success(
        newValue ? 'Notifications enabled' : 'Notifications disabled'
      )
    } catch {
      toast.error('Failed to update notification preference')
    }
    setSaving(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">
          Email notifications
        </h2>
        <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
          <div>
            <p className="font-medium text-gray-900 text-sm">
              CV processing complete
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Save your preference for future processing emails when delivery is available.
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving || loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 ${
              emailEnabled ? 'bg-brand-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                emailEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-yellow-900">
            Billing confirmations are in-app for now
          </p>
          <p className="text-sm text-yellow-700 mt-1">
            Upgrade success is confirmed inside the app after checkout. This toggle stores your email preference,
            but billing confirmation emails should not be relied on until that service is implemented.
          </p>
        </div>
      </div>
    </motion.div>
  )
}

function BillingTab({ onUpgrade }) {
  const navigate = useNavigate()
  const { org, usage } = useOrgStore()
  const { user } = useAuthStore()
  const [loadingPortal, setLoadingPortal] = useState(false)

  const accessPlan = getEffectivePlan({ user, org, usage })
  const subscriptionPlan = getSubscriptionPlan({ user, org, usage })
  const subscriptionStatus = getSubscriptionStatus({ user, org, usage })
  const currentPeriodEnd = getCurrentPeriodEnd({ user, org, usage })
  const cancelAtPeriodEnd = getCancelAtPeriodEnd({ user, org, usage })
  const manualPlanOverride = isManualPlanOverride({ user, org, usage })
  const subscriptionStatusLabel = manualPlanOverride
    ? 'Manual override'
    : (subscriptionStatus || 'processing')
  const billingPlanSummary = formatBillingPlanSummary(subscriptionPlan, {
    cancelAtPeriodEnd,
    currentPeriodEnd,
  })
  const scheduledCancellationMessage = formatScheduledCancellationMessage(
    subscriptionPlan,
    currentPeriodEnd
  )
  const planConfig = config.PLANS[subscriptionPlan] || config.PLANS[accessPlan]
  const isOwner = user?.role === 'owner'
  const isPlatformAdmin = user?.isPlatformAdmin === true
  const canUpgrade = isOwner && !manualPlanOverride && !['enterprise', 'admin'].includes(subscriptionPlan)
  const canManage = isOwner && (
    (!manualPlanOverride && hasManagedBilling({ org, usage })) ||
    PAID_PLANS.includes(subscriptionPlan)
  )
  const canCancel =
    isOwner &&
    !manualPlanOverride &&
    PAID_PLANS.includes(subscriptionPlan) &&
    CANCELABLE_STATUSES.includes(subscriptionStatus || '')

  async function handleManageBilling() {
    setLoadingPortal(true)
    try {
      const data = await stripeApi.createPortal()
      const portalUrl = data.portalUrl || data.url || data.redirectUrl
      if (portalUrl) {
        window.location.href = portalUrl
      } else {
        toast.error('Could not open billing portal')
      }
    } catch (err) {
      toast.error(err.message || 'Could not open billing portal')
    }
    setLoadingPortal(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5"
    >
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Current plan</h2>
          <PlanBadge plan={accessPlan} size="lg" />
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Organisation billing plan</span>
            <span className="font-semibold text-gray-900">
              {billingPlanSummary}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Subscription status</span>
            <span className="font-semibold text-gray-900 capitalize">
              {subscriptionStatusLabel}
            </span>
          </div>
          {currentPeriodEnd && !manualPlanOverride && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {cancelAtPeriodEnd ? 'Access until' : 'Current period ends'}
              </span>
              <span className="font-semibold text-gray-900">{formatDate(currentPeriodEnd)}</span>
            </div>
          )}
        </div>

        {cancelAtPeriodEnd && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
            <p className="font-medium">{scheduledCancellationMessage}</p>
            <p className="mt-2 text-amber-800">
              {BILLING_PORTAL_CANCELLATION_NOTE}
            </p>
          </div>
        )}

        {manualPlanOverride && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900">
            <p className="font-medium">Manual override</p>
            <p className="mt-1 text-blue-800">
              This plan was set directly by an admin. It is not backed by a Stripe subscription.
            </p>
          </div>
        )}

        {isPlatformAdmin && accessPlan === 'admin' && subscriptionPlan !== 'admin' && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900">
            You are viewing this organisation as a platform admin. Billing remains on the
            <span className="font-semibold"> {config.PLANS[subscriptionPlan]?.name || subscriptionPlan}</span>
            {' '}plan while your admin access stays unrestricted.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            {
              label: 'Active Jobs',
              value: usage?.activeJobs ?? 0,
              max: usage?.maxJobs,
            },
            {
              label: 'CVs this month',
              value: usage?.monthlyCVs ?? 0,
              max: usage?.maxCVsPerMonth,
            },
            {
              label: 'Reset date',
              value: formatResetDate(usage?.resetDate),
              max: null,
            },
          ].map((item) => (
            <div key={item.label} className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-900 mt-0.5">
                {item.value}
                {item.max && item.max !== -1 && (
                  <span className="text-gray-400 font-normal text-sm">
                    /{formatNumber(item.max)}
                  </span>
                )}
                {item.max === -1 && (
                  <span className="text-gray-400 font-normal text-sm">
                    /Unlimited
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>

        {planConfig && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Included features
            </p>
            <ul className="space-y-1.5">
              {planConfig.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-center gap-2 text-sm text-gray-600"
                >
                  <Check className="w-4 h-4 text-green-500 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isOwner && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
              {canUpgrade && (
                <Button
                  variant="primary"
                  onClick={onUpgrade}
                  icon={<Zap className="w-4 h-4" />}
                >
                  Upgrade plan
                </Button>
              )}
              {canManage && (
                <Button
                  variant="secondary"
                  loading={loadingPortal}
                  onClick={handleManageBilling}
                  icon={<ExternalLink className="w-4 h-4" />}
                >
                  Open billing portal
                </Button>
              )}
              {canCancel && !cancelAtPeriodEnd && (
                <Button
                  variant="danger"
                  onClick={() => navigate('/dashboard/billing/cancel')}
                >
                  Cancel at period end
                </Button>
              )}
            </div>
            {canManage && (
              <p className="text-xs text-gray-500 pt-3">
                Use the billing portal to update payment details. Cancellation is handled in the app and takes effect at the end of the current billing period.
              </p>
            )}
          </>
        )}

        {!isOwner && (
          <p className="text-sm text-gray-500 pt-4 border-t border-gray-100">
            Only the organisation owner can manage billing.
          </p>
        )}
      </div>

      <div className="card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">
          Compare all plans
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-gray-500 font-medium">
                  Feature
                </th>
                {['free', 'starter', 'pro', 'business'].map((p) => (
                  <th
                    key={p}
                    className={`text-center py-2 font-semibold ${
                      p === subscriptionPlan ? 'text-brand-600' : 'text-gray-700'
                    }`}
                  >
                    {config.PLANS[p]?.name || p}
                    {p === subscriptionPlan && (
                      <span className="ml-1 text-xs font-normal text-brand-500">
                        (current)
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {[
                {
                  label: 'Price',
                  values: ['€0', '€39/mo', '€69/mo', '€129/mo'],
                },
                {
                  label: 'Active jobs',
                  values: ['1', '5', '20', '100'],
                },
                {
                  label: 'CVs/month',
                  values: ['5', '400', '2,000', '10,000'],
                },
                {
                  label: 'CSV export',
                  values: [false, true, true, true],
                },
                {
                  label: 'Candidate compare',
                  values: [false, false, true, true],
                },
                {
                  label: 'Priority processing',
                  values: [false, false, false, true],
                },
              ].map((row) => (
                <tr key={row.label}>
                  <td className="py-2.5 text-gray-600">{row.label}</td>
                  {row.values.map((val, i) => (
                    <td key={i} className="py-2.5 text-center">
                      {typeof val === 'boolean' ? (
                        val ? (
                          <Check className="w-4 h-4 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )
                      ) : (
                        <span
                          className={
                            ['free', 'starter', 'pro', 'business'][i] === subscriptionPlan
                              ? 'font-semibold text-brand-600'
                              : 'text-gray-700'
                          }
                        >
                          {val}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  )
}

function SecurityTab() {
  const { user, logout } = useAuthStore()

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5"
    >
      <div className="card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">Account</h2>
        <div className="space-y-3">
          <div className="form-group">
            <label className="label">Email address</label>
            <input
              type="text"
              className="input bg-gray-50"
              value={user?.email || ''}
              disabled
            />
            <p className="text-xs text-gray-400 mt-1">
              Managed by your identity provider
            </p>
          </div>
          <div className="form-group">
            <label className="label">User ID</label>
            <input
              type="text"
              className="input bg-gray-50 text-xs"
              value={user?.id || ''}
              disabled
            />
          </div>
        </div>
      </div>

      <div className="card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">Password</h2>
        <p className="text-sm text-gray-500 mb-4">
          Your password is managed through Amazon Cognito. Use the link below
          to change it.
        </p>
        <Button
          variant="secondary"
          icon={<ExternalLink className="w-4 h-4" />}
          onClick={redirectToForgotPassword}
        >
          Change password
        </Button>
      </div>

      <div className="card p-5 border-red-100">
        <h2 className="font-semibold text-gray-900 mb-2">Sign out</h2>
        <p className="text-sm text-gray-500 mb-4">
          Sign out of your account on this device.
        </p>
        <Button variant="danger" onClick={logout}>
          Sign out
        </Button>
      </div>
    </motion.div>
  )
}
